import { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import AmountInput from 'components/common/AmountInput';
import { MAX_UPLOADED_FILES_SIZE } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { getMaxDescriptionSize } from 'components/common/PaymentForm/utils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import { getErrorMessageForAlert, strToFloat } from 'services/utils';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import Alert from 'uikit/Alert/Alert';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';

const BottomPartForm = ({
  values,
  errors,
  error,
  ibanValidationError,
  handleChange,
  handleSubmit,
  serverFieldsErrors,
  onAmountIBANChange,
  calculateCommission,
  handleCheckTransfer,
  validateField,
  validateForm,
  isSubmitDisabled,
  isLoading,
  uploadedFiles,
  uploadDocuments,
  removePaymentFile,
  paymentMethod,
  customerNumber,
  currentWalletNumber,
  commission,
  getCommissionValue,
  currency,
  currencyPrecision,
  ibanCredentials,
  isCryptoForm = false,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction,
  model,
  setError,
  shouldShowPaymentPurpose = false,
  paymentPurposeOptions = [],
  hasWalletValidationError = false,
  walletValidationError = null
}) => {
  const i18n = useContext(i18nContext);
  const prevPaymentPurpose = useRef(values?.payment_purpose);
  // A field-attributed ibanValidationError (has `.fields`/`.details`) already renders under its own
  // input via serverFieldsErrors — showing it here too would duplicate the same message. A fieldless
  // one (e.g. "Sort Code not found in bank directory") has nowhere else to render, so surface it here,
  // same as a general `error` would be. See TRN-7316.
  const generalError =
    error ||
    (ibanValidationError && !ibanValidationError?.fields && !ibanValidationError?.details ? ibanValidationError : null);

  // Track payment_purpose changes
  useEffect(() => {
    if (shouldShowPaymentPurpose && values?.payment_purpose && values?.payment_purpose !== prevPaymentPurpose.current) {
      prevPaymentPurpose.current = values?.payment_purpose;
      if (!isForApprovalTransactionEdit) {
        handleCheckTransfer('payment_purpose');
      }
    }
  }, [values?.payment_purpose, shouldShowPaymentPurpose, isForApprovalTransactionEdit, handleCheckTransfer]);

  const handleFileError = (errorCode) => {
    if (setError) {
      setError({
        type: 'attachDoc',
        code: errorCode || 'REQUEST_HAS_BEEN_TERMINATED'
      });
    }
  };

  const handleForApprovalSubmit = async () => {
    // Editing a "For approval" payment skips the per-field server check (checkTransfer), so nothing else would
    // flag an empty/invalid required field before the save request — validate the whole form first and stay
    // on it, with the offending fields highlighted, instead of letting the backend answer 400.
    if (validateForm) {
      const validationErrors = await validateForm();
      if (Object.keys(validationErrors).length) return;
    }
    if (validateForApprovalTransaction) {
      await validateForApprovalTransaction({
        providerData: values,
        model
      });
    }
    setIsPreparedTransactionsCreation(true);
  };

  const handleBlurAmountField = async () => {
    // For crypto forms, check multiple conditions for wallet validation error
    if (isCryptoForm) {
      // Check if there's a wallet validation error in any form
      const hasWalletError =
        hasWalletValidationError ||
        walletValidationError ||
        error?.code === 'CRYPTO_ADDRESS_CHECK_FAILED' ||
        error?.fields?.some((field) => field.field === 'recipientWallet');

      if (hasWalletError) {
        return; // Don't proceed if wallet has validation error
      }
    }

    await calculateCommission(customerNumber, {
      walletNumber: currentWalletNumber,
      paymentMethod: paymentMethod,
      amount: strToFloat(values?.amount?.toString() || ''),
      bankCountry: ibanCredentials?.bank_country || values?.bank_country
    });
    // ForApproval transactions skip the server check (handleCheckTransfer) but still get the client-side one
    if (isForApprovalTransactionEdit) {
      await validateField?.('amount');
    } else {
      await handleCheckTransfer('amount');
    }
  };

  return (
    <>
      <div className={'inputs-wrapper-evenly amount-input-wrapper'}>
        <AmountInput
          // className={''}
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.amount.label')}
          name={'amount'}
          value={values.amount}
          error={errors.amount || serverFieldsErrors.amount}
          onChange={onAmountIBANChange}
          onBlur={handleBlurAmountField}
          precision={currencyPrecision ?? 2}
          currency={currency}
        />
        <Input
          isDisabled={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.commission.label')}
          value={values.amount && commission.value !== '' ? getCommissionValue() : ''}
          onChange={handleChange}
        />
      </div>
      {!isCryptoForm && (
        <div className={'inputs-wrapper'}>
          <Input
            isRequired={true}
            autoComplete={'off'}
            className={'transfer-details'}
            label={i18n.getMessage('transfer.form.transferDetails.label')}
            name={'comment'}
            value={values.comment}
            error={errors.comment || serverFieldsErrors.comment}
            onChange={handleChange}
            onBlur={() => (isForApprovalTransactionEdit ? validateField?.('comment') : handleCheckTransfer('comment'))}
            max={getMaxDescriptionSize(paymentMethod)}
          />
        </div>
      )}
      {shouldShowPaymentPurpose && (
        <InputDropDownSearch
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.paymentPurpose.label')}
          name={'payment_purpose'}
          value={values.payment_purpose}
          onChange={(name, value) => handleChange({ target: { name, value } })}
          options={paymentPurposeOptions}
          error={errors.payment_purpose || serverFieldsErrors?.payment_purpose}
        />
      )}

      {/* Render CustomFileDragAndDrop for all transaction types and ForApproval button separately */}
      <div className={'attach-doc-wrapper'}>
        <CustomFileDragAndDrop
          files={uploadedFiles}
          onChange={uploadDocuments}
          onRemoveFiles={removePaymentFile}
          onError={handleFileError}
          maxSize={MAX_UPLOADED_FILES_SIZE}
          error={error?.type === 'attachDoc' ? getErrorMessageForAlert(i18n, error) : null}
        />

        {isForApprovalTransactionEdit && (
          <Button
            className={'save-transfer-button'}
            type={'primary'}
            roleType={'button'}
            size={'large'}
            onClick={handleForApprovalSubmit}
            isDisabled={isSubmitDisabled}
          >
            {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.save')}
          </Button>
        )}
      </div>

      <Alert
        className={'send-money-alert send-money-alert-with-attachment'}
        type={'warning'}
        message={
          generalError && !generalError?.type
            ? getErrorMessageForAlert(i18n, generalError, true, paymentMethod, serverFieldsErrors)
            : ''
        }
      />

      {!isForApprovalTransactionEdit && (
        <div className={'inputs-wrapper-evenly action-buttons-wrapper'}>
          {isUserCanCreatePreparedTransaction && (
            <Button
              className={'send-transfer-button'}
              type={'outline'}
              roleType={'button'}
              size={'large'}
              onClick={() => {
                setIsPreparedTransactionsCreation(true);
                handleSubmit();
              }}
              isDisabled={isSubmitDisabled || !isUserCanCreatePreparedTransaction}
            >
              {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.saveForApproval')}
            </Button>
          )}
          <Button
            className={`${!isUserCanCreatePreparedTransaction ? 'send-transfer-button-half' : 'send-transfer-button'}`}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled || !isUserCanSendTransaction}
          >
            {isLoading ? <Loader /> : i18n.getMessage('sendMoney.button.send')}
          </Button>
        </div>
      )}
    </>
  );
};

BottomPartForm.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  error: PropTypes.any,
  ibanValidationError: PropTypes.any,
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  serverFieldsErrors: PropTypes.object,
  onAmountIBANChange: PropTypes.func,
  calculateCommission: PropTypes.func,
  handleCheckTransfer: PropTypes.func,
  validateField: PropTypes.func,
  validateForm: PropTypes.func,
  getCommissionValue: PropTypes.func,
  isSubmitDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removePaymentFile: PropTypes.func,
  paymentProvider: PropTypes.string,
  paymentMethod: PropTypes.string,
  currency: PropTypes.string,
  customerNumber: PropTypes.string,
  currentWalletNumber: PropTypes.string,
  commission: PropTypes.object,
  ibanCredentials: PropTypes.object,
  isCryptoForm: PropTypes.bool,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func,
  model: PropTypes.func,
  setError: PropTypes.func,
  shouldShowPaymentPurpose: PropTypes.bool,
  paymentPurposeOptions: PropTypes.array,
  hasWalletValidationError: PropTypes.bool,
  walletValidationError: PropTypes.any,
  validateWallet: PropTypes.func,
  lastValidatedWallet: PropTypes.string
};

export default BottomPartForm;
